import type { BadgeProps } from "@rumbledirect/ui-v2";
import { EMPTY_VALUE } from "@rumbledirect/ui-v2/format";
import type {
  VehicleHandoverDestination,
  VehicleHandoverReason,
  VehicleHandoverScope,
  VehicleHandoverStatus,
  VehicleHandoverTransition,
} from "./vehicleHandover";

/**
 * Dutch labels for the vehicle handover enums, mirroring the backend's
 * `messages.nl.yaml`. Each map is ordered the way the backend enum is, so a
 * select or filter row built from it reads in the same order as the API
 * documents it.
 */
export const handoverStatusLabels: Record<VehicleHandoverStatus, string> = {
  to_process: "Te verwerken",
  planned: "Gepland",
  completed: "Afgerond",
};

export const handoverWorkLabels = {
  overdue: "Te laat",
  followUpOverdue: "Vervolgactie te laat",
  partiallyCompleted:
    "Een zijde is afgerond. De andere staat nog open; bevestig de openstaande overdracht afzonderlijk.",
  completedSwap: "Beide zijden zijn afgerond.",
} as const;

export const handoverReasonLabels: Record<
  NonNullable<VehicleHandoverReason>,
  string
> = {
  contract_end: "Einde contract",
  early_termination: "Voortijdige beëindiging",
  employee_change: "Medewerkerwissel",
  damage: "Schade",
  other: "Overig",
  employment_end: "Uitdiensttreding",
  entitlement_lapsed: "Aanspraak vervallen",
  vehicle_swap: "Voertuigwissel",
  temporary_mobility_end: "Einde tijdelijke mobiliteit",
};

export const handoverDestinationLabels: Record<
  NonNullable<VehicleHandoverDestination>,
  string
> = {
  lessor: "Terug naar leasemaatschappij",
  fleet: "Poolbeheer",
};

/**
 * The destination a reason usually leads to, mirroring the backend enum's
 * `defaultDestination()`. "Overig" says nothing about where the vehicle goes,
 * so it is left to be chosen rather than guessed.
 */
export function defaultHandoverDestination(
  reason: VehicleHandoverReason,
): VehicleHandoverDestination | null {
  switch (reason) {
    case "contract_end":
    case "early_termination":
      return "lessor";
    case "employee_change":
    case "damage":
      return "fleet";
    default:
      return null;
  }
}

/** Labels for the action buttons, so a step reads as the deed it performs. */
export const handoverTransitionLabels: Record<
  VehicleHandoverTransition,
  string
> = {
  plan: "Inname plannen",
  send_instructions: "Instructies versturen",
  complete: "Inname bevestigen",
};

/**
 * Narrows a step offered by the API onto the steps this app knows how to
 * render. A step added to the backend before this list is dropped rather than
 * shown as a button nobody wrote a label or a form for.
 */
export function isVehicleHandoverTransition(
  value: string,
): value is VehicleHandoverTransition {
  return Object.hasOwn(handoverTransitionLabels, value);
}

/**
 * Resolves a raw enum value to its Dutch label. Falls back to the raw value so
 * a value added to the backend before this list still reads as something, and
 * to a dash when there is nothing to show at all.
 */
function labelFrom(
  labels: Record<string, string>,
  value: string | null | undefined,
): string {
  if (!value) return EMPTY_VALUE;
  return labels[value] ?? value;
}

export function handoverStatusLabel(value: string | null | undefined): string {
  return labelFrom(handoverStatusLabels, value);
}

export function handoverReasonLabel(value: string | null | undefined): string {
  return labelFrom(handoverReasonLabels, value);
}

export function handoverDestinationLabel(
  value: string | null | undefined,
): string {
  return labelFrom(handoverDestinationLabels, value);
}

export function handoverTransitionLabel(
  value: string | null | undefined,
): string {
  return labelFrom(handoverTransitionLabels, value);
}

/** The statuses as ordered options for a filter or select. */
export const handoverStatusOptions: readonly {
  value: VehicleHandoverStatus;
  label: string;
}[] = (Object.keys(handoverStatusLabels) as VehicleHandoverStatus[]).map(
  (value) => ({ value, label: handoverStatusLabels[value] }),
);

/** The destinations as ordered options for a filter or select. */
export const handoverDestinationOptions: readonly {
  value: NonNullable<VehicleHandoverDestination>;
  label: string;
}[] = (
  Object.keys(
    handoverDestinationLabels,
  ) as NonNullable<VehicleHandoverDestination>[]
).map((value) => ({ value, label: handoverDestinationLabels[value] }));

/** The reasons as ordered options for a select. */
export const handoverReasonOptions: readonly {
  value: NonNullable<VehicleHandoverReason>;
  label: string;
}[] = (
  Object.keys(handoverReasonLabels) as NonNullable<VehicleHandoverReason>[]
).map((value) => ({ value, label: handoverReasonLabels[value] }));

/**
 * What the overview's status filter can be set to: one exact status, or one of
 * the two groups around them.
 */
export type VehicleHandoverStatusFilter =
  | "open"
  | "all"
  | VehicleHandoverStatus;

/**
 * The overview's status filter as one list. "Actueel" and "Alle" sit around the
 * exact statuses rather than in a filter of their own: they answer the same
 * question ("which hand-ins am I looking at?"), and two controls for one
 * question only invite contradicting answers.
 */
export const handoverStatusFilterOptions: readonly {
  value: VehicleHandoverStatusFilter;
  label: string;
}[] = [
  { value: "open", label: "Actueel" },
  ...handoverStatusOptions,
  { value: "all", label: "Alle" },
];

/**
 * The view the overview opens on, and the one anything linking back to it
 * should land on. Named here so the filter and every such link agree.
 */
export const DEFAULT_HANDOVER_STATUS_FILTER: VehicleHandoverStatusFilter =
  "open";

/**
 * The query the API needs for a filter value. "Actueel" is the scope the
 * backend already knows; an exact status is asked for across everything,
 * because a status is only ever in one group anyway and narrowing twice would
 * silently return nothing.
 */
export function handoverStatusFilterQuery(value: VehicleHandoverStatusFilter): {
  scope: VehicleHandoverScope;
  status?: VehicleHandoverStatus;
} {
  if (value === "open" || value === "all") {
    return { scope: value };
  }

  return { scope: "all", status: value };
}

/**
 * Tone for a status badge. The process reads as three phases: still being
 * arranged (neutral), under way (info) and settled (done), so a row's stage is
 * visible at a glance.
 */
export function handoverStatusTone(
  value: string | null | undefined,
): NonNullable<BadgeProps["variant"]> {
  switch (value) {
    case "planned":
      return "info";
    case "completed":
      return "done";
    default:
      return "neutral";
  }
}
